/**
 * Maps bifrost events to Redux actions.
 *
 * A user receives events for every claim they act on, plus whichever claim the
 * tab has open, so a tab routinely receives events for claims it is not
 * showing. Relevance is therefore decided here, per tab, by comparing the
 * event's entity against the ids in this tab's own URL -- and the nudges that
 * reach components carry the claim id, so nothing inside an open claim reacts
 * to another claim's traffic.
 *
 * A handler receives ({ envelope, getState }) and returns an array of actions
 * (possibly empty). Event names are the real NATS subjects from
 * core-kit/constant/topic/topic.go -- not invented ones.
 */

import { getNewNotificationCount } from '../containers/Header/actions/action';
import {
    getWorkkardDetailMasterData,
    getActivityDetail,
} from '../containers/WorkkardDetail/actions/commonAction';
import {
    updateUserView,
    updateOrganizationView,
    fetchSessionContextOidcRequest,
    refreshCurrentUserRequest,
} from '../common/reducers/sessionContextOidc';
import { orgProfileImageChanged } from '../containers/AdminPanel/orgProfile/orgProfileSlice';
import { imageGuid, withImageGuid } from '../common/util';
import { liveEntityChanged, liveActivitySelected } from '../common/slice/liveUpdates';
import { upsertUsers } from '../common/slice/userList';

/* ------------------------------------------------------------------ *
 * Relevance helpers
 * ------------------------------------------------------------------ */

// Read from the URL rather than Redux: on a detail page that is still loading,
// the store slice is empty but the URL already names the claim.
const queryParam = (key) => {
    try {
        return new URLSearchParams(window.location.search).get(key);
    } catch (e) {
        return null;
    }
};

const tabClaimId = () => queryParam('cId');
const tabActivityId = () => queryParam('actId');

// Ids cross the wire as numbers and appear in the URL as strings.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const isThisClaim = (entity) => sameId(entity?.workkardId, tabClaimId());

// The URL is not the only record of the open activity: a reassignment that
// hands the work to a new activity loads it without rewriting the URL, so the
// activity the tab has loaded counts too.
const loadedActivityId = (getState) => getState?.()?.workkardDetail?.activityDetail?.activityId;
const isThisActivity = (entity, getState) =>
    sameId(entity?.activityId, tabActivityId()) || sameId(entity?.activityId, loadedActivityId(getState));

// Entity spells its fields in camelCase, but the services behind it do not all
// agree, so both spellings are read rather than assuming one.
const entityField = (entity, camel, snake) => entity?.[camel] ?? entity?.[snake] ?? null;

/**
 * Whether this user is allowed to see the activity an event names.
 *
 * An activity whose owner org and assignee org differ belongs to the owner org
 * alone, so its events are dropped for everyone else -- refreshing on them would
 * only ask for records the read side will not return. An activity held by a
 * single org stays visible to it, and an event that names no orgs at all cannot
 * be judged here and is left to the read side to filter.
 */
const isActivityVisible = (entity, getState) => {
    const ownerOrgId = entityField(entity, 'ownerOrgId', 'owner_org_id');
    const assigneeOrgId = entityField(entity, 'assigneeOrgId', 'assignee_org_id');
    if (ownerOrgId == null || assigneeOrgId == null) return true;
    if (sameId(ownerOrgId, assigneeOrgId)) return true;
    return sameId(ownerOrgId, getState()?.sessionContextOidc?.userView?.orgId);
};

/* ------------------------------------------------------------------ *
 * Shared refreshes
 * ------------------------------------------------------------------ */

// The claim master data -- header, fields, action menu. The payload is entirely
// URL-derived, so it is safe to build here, unlike the panels' own queries.
const refreshClaimDetail = (entity) =>
    (isThisClaim(entity)
        ? [getWorkkardDetailMasterData({
            cId: tabClaimId(),
            actId: tabActivityId() || undefined,
            silent: true,
            doNotUpdateActivityListing: true,
            doNotUpdateNoteFilters: true,
            doNotUpdateSessionContext: true,
        })]
        : []);

// Only the tab that has this very activity open refetches its detail; the
// listing row is refreshed by the activity nudge instead.
const refreshActivityDetail = (entity, getState) =>
    (isThisClaim(entity) && isThisActivity(entity, getState)
        ? [getActivityDetail({ actId: entity.activityId, force: true })]
        : []);

// Listings and panels own their own query arguments -- pagination, the selected
// notes tab, review filters -- which cannot be reconstructed here, so they are
// nudged rather than dispatched at. The claim id travels with the nudge so a
// panel can ignore other claims. See app/common/slice/liveUpdates.js.
const nudge = (domain, entity) => [liveEntityChanged({ domain, workkardId: entity?.workkardId })];

/* ------------------------------------------------------------------ *
 * Handlers
 * ------------------------------------------------------------------ */

const workkardChanged = ({ envelope }) => refreshClaimDetail(envelope.entity);

// A claim opening or closing changes which actions its activities offer, so the
// activity listing has to be refreshed too -- an in-place field edit does not.
const workkardLifecycleChanged = ({ envelope }) => [
    ...refreshClaimDetail(envelope.entity),
    ...nudge('workkard', envelope.entity),
    ...nudge('activity', envelope.entity),
];

// An in-place activity change -- a field edit, an acceptance, a progress step --
// leaves the claim's own fields alone, so the claim master data is not refetched
// for it. The listing refreshes itself through the nudge.
const activityChanged = ({ envelope, getState }) => {
    if (!isActivityVisible(envelope.entity, getState)) return [];
    return [
        ...refreshActivityDetail(envelope.entity, getState),
        ...nudge('activity', envelope.entity),
    ];
};

// Creating, completing, closing or reopening an activity does change the claim
// itself -- its counts and its available actions -- so these also refresh the
// claim header.
const activityLifecycleChanged = ({ envelope, getState }) => {
    if (!isActivityVisible(envelope.entity, getState)) return [];
    return [
        ...refreshClaimDetail(envelope.entity),
        ...refreshActivityDetail(envelope.entity, getState),
        ...nudge('activity', envelope.entity),
    ];
};

const isOwnCreate = (entity, getState) =>
    sameId(entity?.activityId, getState()?.activityForm?.createActivity?.pendingActivityId);


const activityCreated = ({ envelope, getState }) => {
    const entity = envelope.entity;
    if (!isActivityVisible(entity, getState)) return [];

    const actions = activityLifecycleChanged({ envelope, getState });
    if (isThisClaim(entity) && entity?.activityId && isOwnCreate(entity, getState)) {
        actions.push(liveActivitySelected({
            workkardId: entity.workkardId,
            activityId: entity.activityId,
        }));
    }
    return actions;
};

const noteChanged = ({ envelope }) => nudge('note', envelope.entity);

const diaryChanged = ({ envelope }) => nudge('diary', envelope.entity);

const USER_EVENT_TO_USERLIST_FIELD = {
    firstName: 'first_name',
    lastName: 'last_name',
    userName: 'user_name',
    orgId: 'org_id',
    profileImage: 'profile_image',
    isActive: 'is_active',
    createdBy: 'created_by',
    createdDate: 'created_date',
    updatedBy: 'updated_by',
    updatedDate: 'updated_date',
    userRole: 'user_role',
    supervisorId: 'supervisor_id',
};

const toUserListEntry = (data) => {
    const aliased = {};
    Object.entries(USER_EVENT_TO_USERLIST_FIELD).forEach(([camelKey, snakeKey]) => {
        if (data[camelKey] !== undefined) aliased[snakeKey] = data[camelKey];
    });
    return { ...data, ...aliased };
};

// The event carries the profile image as a bare file name -- and omits it
// entirely once the image is removed -- whereas the read side serves a URL
// (a default one after removal). The event's value is therefore never stored;
// when it disagrees with the URL held, the current user is re-read instead.
// Only `user.updated` carries the full record, so only it is judged this way.
const userChanged = ({ envelope, getState }) => {
    const actions = nudge('user');
    const data = envelope.data;
    const changedUserId = data?.id;
    if (!data) return actions;

    const { profileImage, ...rest } = data;

    if (changedUserId != null) {
        actions.push(upsertUsers({ [changedUserId]: toUserListEntry(rest) }));
    }

    const userView = getState()?.sessionContextOidc?.userView;
    if (sameId(changedUserId, userView?.id)) {
        const orgChanged = data.orgId !== undefined && !sameId(data.orgId, userView?.orgId);
        if (orgChanged) {
            actions.push(fetchSessionContextOidcRequest());
        } else {
            actions.push(updateUserView(rest));
            const imageChanged = envelope.event === 'user.updated'
                && (profileImage ?? null) !== imageGuid(userView?.profileImage);
            if (imageChanged) actions.push(refreshCurrentUserRequest());
        }
    }
    return actions;
};

const organizationChanged = () => nudge('organization');

// The read side can lag behind this event, so the refetch it triggers may still
// serve the previous logo. The announced file name is therefore applied here and
// held as pending until a refetch returns it. The event omits the image once it
// is removed, which clears the pending one so the refetched default is accepted.
const organizationUpdated = ({ envelope, getState }) => {
    const actions = organizationChanged();
    const data = envelope.data;
    const organizationView = getState()?.sessionContextOidc?.organizationView;
    if (!data || !sameId(data.id, organizationView?.Id)) return actions;

    const profileImage = data.profileImage ?? null;
    actions.push(orgProfileImageChanged(profileImage));
    const url = profileImage && withImageGuid(organizationView?.ProfileFullImage, profileImage);
    if (url) actions.push(updateOrganizationView({ ProfileFullImage: url }));
    return actions;
};

const partyChanged = ({ envelope }) => [
    ...refreshClaimDetail(envelope.entity),
    ...nudge('party', envelope.entity),
];

const fileChanged = ({ envelope }) => [
    liveEntityChanged({ domain: 'file', workkardId: envelope?.entity?.workkardId || tabClaimId() || undefined }),
];

const notificationCreated = () => [getNewNotificationCount()];

const isSponsoredByMe = (envelope, getState) =>
    sameId(envelope?.data?.workkard?.sponsor, getState()?.sessionContextOidc?.userView?.id);

const workkardCreated = ({ envelope, getState }) => [
    ...refreshClaimDetail(envelope.entity),
    ...(isSponsoredByMe(envelope, getState) ? nudge('workkard', envelope.entity) : []),
];

// A user's block state moved the org-wide unassigned counts. Only a mounted
// listing reacts to the nudge, so users on other routes refetch nothing.
const workkardCountsChanged = () => nudge('workkard');
const activityCountsChanged = () => nudge('activity');

const eventHandlers = {
    // workkard.*
    'workkard.created': workkardCreated,
    'workkard.updated': workkardChanged,
    'workkard.reopened': workkardLifecycleChanged,
    'workkard.closed': workkardLifecycleChanged,
    'workkard.open': workkardLifecycleChanged,
    'workkard.sponsor.changed': workkardChanged,
    'workkard.counts.updated': workkardCountsChanged,

    // activity.*
    'activity.created': activityCreated,
    'activity.updated': activityChanged,
    'activity.owner_changed': activityChanged,
    'activity.reassigned': activityChanged,
    'activity.accepted': activityChanged,
    'activity.rejected': activityChanged,
    'activity.completed': activityLifecycleChanged,
    'activity.closed': activityLifecycleChanged,
    'activity.closed_mid_term': activityLifecycleChanged,
    'activity.reopened': activityLifecycleChanged,
    'activity.in_progress': activityChanged,
    'activity.in_progress_done': activityChanged,
    'activity.counts.updated': activityCountsChanged,

    // note.* -- note events are named after the module that owns the note
    'workkard.public_note_added': noteChanged,
    'workkard.community_note_added': noteChanged,
    'workkard.note_published': noteChanged,
    'workkard.note_deleted': noteChanged,
    'activity.public_note_added': noteChanged,
    'activity.community_note_added': noteChanged,
    'activity.note_published': noteChanged,
    'activity.note_deleted': noteChanged,
    'activity.notes.reviewed.bulk': noteChanged,
    'note.reviewed': noteChanged,

    // diary.*
    'diary.created': diaryChanged,
    'diary.updated': diaryChanged,
    'diary.completed': diaryChanged,
    'diary.reopened': diaryChanged,
    'diary.cancelled': diaryChanged,
    'diary.reassigned': diaryChanged,
    'diary.duedate_changed': diaryChanged,
    'diary.priority_changed': diaryChanged,

    // user.*
    'user.created': userChanged,
    'user.updated': userChanged,
    'user.blocked': userChanged,
    'user.unblocked': userChanged,
    'user.deactivated': userChanged,
    'user.timezone.updated': userChanged,
    'user.permission.updated': userChanged,

    // organization.*
    'organization.updated': organizationUpdated,
    'organization.servicelocation.updated': organizationChanged,
    'organization.skillset.updated': organizationChanged,
    'organization.communication.updated': organizationChanged,
    'organization.community.created': organizationChanged,
    'organization.community.updated': organizationChanged,
    'organization.b2b.created': organizationChanged,
    'organization.b2b.updated': organizationChanged,

    // party.*
    'party.created': partyChanged,
    'party.updated': partyChanged,
    'party.deleted': partyChanged,
    'party.activated': partyChanged,
    'party.deactivated': partyChanged,
    'party.workkard.associated': partyChanged,
    'party.activity.associated': partyChanged,
    'party.workkard.disassociated': partyChanged,
    'party.activity.disassociated': partyChanged,

    // file.*
    'file.caption.updated': fileChanged,

    // notifier
    'notification.created': notificationCreated,
};

/**
 * Resolves an envelope to the actions it should produce.
 * Unknown events -- and bifrost's own control frames -- yield nothing.
 */
export const resolveActions = (envelope, getState) => {
    const handler = eventHandlers[envelope?.event];
    if (!handler) return [];
    try {
        const actions = handler({ envelope, getState });
        return Array.isArray(actions) ? actions.filter(Boolean) : [actions].filter(Boolean);
    } catch (e) {
        console.error(`[bifrost] handler for ${envelope.event} threw`, e);
        return [];
    }
};

export { isThisClaim, isThisActivity, tabClaimId, tabActivityId };
export default eventHandlers;
